import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

// Simple mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Card animation variants matching dashboard design - mobile optimized
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Mobile-optimized motion component
const MobileOptimizedMotion = ({ children, className, variants, whileHover, transition, initial, animate, exit, ...props }: any) => {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    // Return a regular div on mobile
    return <div className={className}>{children}</div>;
  }
  // Return motion.div on desktop
  return (
    <motion.div 
      className={className}
      variants={variants}
      initial={initial}
      animate={animate}
      exit={exit}
      whileHover={whileHover}
      transition={transition}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Enhanced RLUSD Token component
const RLUSDToken = ({ size = "w-8 h-8" }: { size?: string }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="url(#rlusd-gradient)" stroke="currentColor" strokeWidth="1"/>
    <text x="12" y="16" textAnchor="middle" className="fill-white text-xs font-bold">R</text>
    <defs>
      <linearGradient id="rlusd-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{stopColor: '#3B82F6'}} />
        <stop offset="100%" style={{stopColor: '#1E40AF'}} />
      </linearGradient>
    </defs>
  </svg>
);

// Enhanced Add Funds Modal with dashboard design theme
const AddFundsModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const isMobile = useIsMobile();
  const [amount, setAmount] = useState('');
  const [selectedTranche, setSelectedTranche] = useState<'senior' | 'junior'>('senior');
  const [showTransactionSummary, setShowTransactionSummary] = useState(false);
  const availableBalance = 5000;

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleMaxClick = () => {
    setAmount(availableBalance.toString());
  };

  const isValidAmount = amount && parseFloat(amount) > 0 && parseFloat(amount) <= availableBalance;

  const handleConfirmDeposit = () => {
    // Handle deposit logic here
    onClose();
    setAmount('');
    setShowTransactionSummary(false);
  };

  const modalContent = (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-slate-900/80 backdrop-blur-xl flex items-center justify-center z-[9999] p-4">
      <MobileOptimizedMotion 
        className="rounded-xl p-5 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>
        
        <div className="relative z-10">
          {/* Modal Header */}
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-xl font-semibold text-white">
              Add Funds to <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Vault</span>
            </h2>
            <button 
              onClick={onClose} 
              className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Amount Input */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Amount</label>
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 text-lg font-semibold text-white placeholder-slate-400 focus:border-blue-500/40 focus:outline-none transition-all duration-500" 
                />
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 bg-slate-800/50 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 border border-slate-700/50">
                  <img src="/images/RLUSD.png" alt="RLUSD" className="w-4 h-4 rounded-full object-cover" />
                  <span className="font-medium text-xs text-white">RLUSD</span>
                </div>
              </div>
              <div className="flex justify-between text-xs mt-1.5">
                <span className="text-slate-400">Available: {availableBalance.toLocaleString()} RLUSD</span>
                <button 
                  onClick={handleMaxClick}
                  className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  Max
                </button>
              </div>
            </div>
            
            {/* Tranche Selection */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Select Tranche</label>
              <div className="grid grid-cols-2 gap-2.5">
                <MobileOptimizedMotion 
                  onClick={() => setSelectedTranche('senior')}
                  className={`p-3 rounded-xl border transition-all duration-300 cursor-pointer ${
                    selectedTranche === 'senior'
                      ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-white'
                      : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-blue-400/40'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="text-xs font-medium mb-1">Senior Tranche</div>
                  <div className="text-sm font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">4.2% APY</div>
                </MobileOptimizedMotion>
                <MobileOptimizedMotion 
                  onClick={() => setSelectedTranche('junior')}
                  className={`p-3 rounded-xl border transition-all duration-300 cursor-pointer ${
                    selectedTranche === 'junior'
                      ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-white'
                      : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-blue-400/40'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="text-xs font-medium mb-1">Junior Tranche</div>
                  <div className="text-sm font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">6.8% APY</div>
                </MobileOptimizedMotion>
              </div>
            </div>
            
            {/* Collapsible Transaction Summary */}
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50">
              <button
                onClick={() => setShowTransactionSummary(!showTransactionSummary)}
                className="w-full p-3 flex justify-between items-center hover:bg-slate-700/30 transition-colors duration-300 rounded-xl"
              >
                <h4 className="font-medium text-white text-sm">Transaction Summary</h4>
                <motion.div 
                  className="text-slate-400"
                  animate={isMobile ? {} : { rotate: showTransactionSummary ? 180 : 0 }}
                  transition={isMobile ? {} : { duration: 0.2 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                  </svg>
                </motion.div>
              </button>
              
              <motion.div 
                initial={false}
                animate={isMobile ? 
                  { height: showTransactionSummary ? 'auto' : 0, opacity: showTransactionSummary ? 1 : 0 } :
                  { height: showTransactionSummary ? 'auto' : 0, opacity: showTransactionSummary ? 1 : 0 }
                }
                transition={isMobile ? { duration: 0 } : { duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="px-3 pb-3 space-y-2 text-xs border-t border-slate-700/50">
                  <div className="pt-2 flex justify-between">
                    <span className="text-slate-400">Your Balance</span>
                    <span className="font-medium text-white">{availableBalance.toLocaleString()} RLUSD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Selected Tranche</span>
                    <span className="font-medium text-white capitalize">{selectedTranche}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Current APY</span>
                    <span className="font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                      {selectedTranche === 'senior' ? '4.2%' : '6.8%'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Risk Level</span>
                    <span className="font-medium text-white">{selectedTranche === 'senior' ? 'Lower' : 'Higher'}</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Confirm Button */}
            <MobileOptimizedMotion 
              onClick={handleConfirmDeposit}
              className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-300 ${
                isValidAmount
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25'
                  : 'bg-slate-700/50 text-slate-400 cursor-not-allowed'
              }`}
              disabled={!isValidAmount}
              whileHover={isValidAmount ? { scale: 1.02, boxShadow: "0 0 30px rgba(59, 130, 246, 0.4)" } : undefined}
              whileTap={isValidAmount ? { scale: 0.98 } : undefined}
            >
              {isValidAmount ? 'Confirm Deposit' : 'Enter Amount'}
            </MobileOptimizedMotion>
          </div>
        </div>
      </MobileOptimizedMotion>
    </div>
  );

  return createPortal(modalContent, document.body);
};

const Vault: React.FC = () => {
  const isMobile = useIsMobile();
  const [selectedTranche, setSelectedTranche] = useState<'senior' | 'junior'>('senior');
  const [amount, setAmount] = useState('');
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [showTransactionSummary, setShowTransactionSummary] = useState(false);

  // Real-time data simulation
  const [vaultBalance, setVaultBalance] = useState(156800);
  const [earnedInterest, setEarnedInterest] = useState(6734.50);
  const [currentAPR, setCurrentAPR] = useState(4.5);
  const [totalLocked, setTotalLocked] = useState(24500000);
  const [activeDeposits, setActiveDeposits] = useState(1247);

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setEarnedInterest(prev => prev + (Math.random() * 0.5));
      setCurrentAPR(prev => prev + (Math.random() - 0.5) * 0.01);
      setTotalLocked(prev => prev + (Math.random() - 0.5) * 5000);
      setActiveDeposits(prev => prev + Math.floor((Math.random() - 0.5) * 3));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleAddFunds = () => {
    setShowAddFundsModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header Section with Dashboard Theme - Entry animation disabled */}
      <MobileOptimizedMotion 
        className="relative overflow-hidden rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        whileHover={{ 
          scale: 1.01,
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>
        
        <div className="relative z-10">
          <h1 className="text-2xl font-semibold text-white tracking-tight mb-3">
            Yield <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Vaults</span>
          </h1>
          <p className="text-slate-300 mb-6 max-w-2xl">
            Earn yield on your RLUSD through institutional-grade DeFi strategies with XRP neobank security
          </p>
          
          {/* Stats Grid with Dashboard Design */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              { 
                title: 'TOTAL VAULT TVL', 
                value: `$${(totalLocked / 1000000).toFixed(1)}M`, 
                subtitle: 'Across all tranches',
                progress: 65
              },
              { 
                title: 'CURRENT YIELD', 
                value: `${currentAPR.toFixed(1)}%`, 
                subtitle: 'Weighted average APY',
                progress: 45
              },
              { 
                title: 'YOUR POSITION', 
                value: `$${vaultBalance.toLocaleString()}`, 
                subtitle: `+$${earnedInterest.toFixed(0)} earned`,
                progress: 75
              }
            ].map((item, i) => (
              <MobileOptimizedMotion 
                key={i}
                className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">{item.title}</div>
                <div className="text-2xl font-semibold text-white mb-1">{item.value}</div>
                <div className="text-sm text-slate-300 mb-3">{item.subtitle}</div>
                
                {/* Progress bar with dashboard styling */}
                <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 h-1.5 rounded-full shadow-lg shadow-blue-500/50"
                    initial={{ width: isMobile ? `${item.progress}%` : 0 }}
                    animate={{ width: `${item.progress}%` }}
                    transition={isMobile ? { duration: 0 } : { duration: 1.5, delay: 0.5 + i * 0.1 }}
                  />
                </div>
              </MobileOptimizedMotion>
            ))}
          </div>
        </div>
      </MobileOptimizedMotion>

      {/* Main Interface Container with Dashboard Theme */}
      <MobileOptimizedMotion 
        className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>

        <div className="relative z-10 p-6 space-y-6">
          {/* Interface Title */}
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-white mb-2">Vault Interface</h2>
            <p className="text-sm text-slate-300">Deposit RLUSD to start earning yield</p>
          </div>

          {/* Vault Deposit Interface */}
          <MobileOptimizedMotion 
            className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50"
            variants={cardVariants}
          >
            <h3 className="text-lg font-semibold text-white mb-4">Deposit Funds</h3>
            
            {/* Amount Input */}
            <div className="bg-slate-800/50 rounded-xl p-4 mb-4 border border-slate-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-400">Amount</span>
                <span className="text-xs text-slate-400">Balance: 1,250.00 RLUSD</span>
              </div>
              <div className={`flex items-center gap-3 ${isMobile ? 'relative' : ''}`}>
                <input
                  type="text"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={`flex-1 bg-transparent text-2xl font-semibold text-white placeholder-slate-400 border-none outline-none ${isMobile ? 'pr-20' : ''}`}
                />
                <div className={`flex items-center gap-2 ${isMobile ? 'absolute right-2 top-1/2 -translate-y-1/2' : ''}`}>
                  <img src="/images/RLUSD.png" alt="RLUSD" className="w-6 h-6 rounded-full object-cover border border-blue-500/20" />
                  <span className="text-sm font-medium text-white">RLUSD</span>
                </div>
              </div>
            </div>

            {/* Tranche Selection */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-400 mb-3 block uppercase tracking-wider">Select Tranche</label>
              <div className="grid grid-cols-2 gap-3">
                <MobileOptimizedMotion
                  onClick={() => setSelectedTranche('senior')}
                  className={`p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                    selectedTranche === 'senior'
                      ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-white'
                      : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-blue-400/40'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="text-sm font-medium text-white">Senior Tranche</div>
                  <div className="text-xs bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent font-semibold">4.2% APY</div>
                </MobileOptimizedMotion>
                <MobileOptimizedMotion
                  onClick={() => setSelectedTranche('junior')}
                  className={`p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                    selectedTranche === 'junior'
                      ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-white'
                      : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-blue-400/40'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="text-sm font-medium text-white">Junior Tranche</div>
                  <div className="text-xs bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent font-semibold">6.8% APY</div>
                </MobileOptimizedMotion>
              </div>
            </div>

            {/* Deposit Button */}
            <MobileOptimizedMotion 
              onClick={handleAddFunds}
              className="w-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white font-medium py-3 px-6 rounded-xl transition-all duration-300 relative overflow-hidden group cursor-pointer"
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 0 30px rgba(59, 130, 246, 0.3)"
              }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <span className="relative z-10 text-sm font-medium">Add Funds to Vault</span>
            </MobileOptimizedMotion>
          </MobileOptimizedMotion>
        </div>
      </MobileOptimizedMotion>

      {/* Vault Performance Section with Dashboard Theme */}
      <MobileOptimizedMotion 
        className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>

        <div className="relative z-10 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Your Vault Positions</h2>
          <div className="space-y-3">
            {[
              { tranche: 'Senior', amount: '50,000 RLUSD', apy: '4.2%', earned: '+2,100 RLUSD', status: 'Active' },
              { tranche: 'Junior', amount: '25,000 RLUSD', apy: '6.8%', earned: '+1,700 RLUSD', status: 'Active' },
              { tranche: 'Senior', amount: '75,000 RLUSD', apy: '4.5%', earned: '+3,375 RLUSD', status: 'Matured' }
            ].map((position, i) => (
              <MobileOptimizedMotion 
                key={i} 
                className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 hover:border-blue-500/40 transition-all duration-500"
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.01,
                  boxShadow: "0 0 20px rgba(59, 130, 246, 0.1)"
                }}
              >
                <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-center justify-between'}`}>
                  <div className="flex items-center gap-4">
                    <MobileOptimizedMotion 
                      className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/40 flex items-center justify-center"
                      whileHover={{ scale: 1.1 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-blue-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                    </MobileOptimizedMotion>
                    <div className={isMobile ? 'flex-1' : ''}>
                      <div className="font-medium text-white text-sm">{position.tranche} Tranche - {position.amount}</div>
                      <div className={`text-xs text-slate-300 ${isMobile ? 'mt-1' : ''}`}>APY: {position.apy} • Earned: <span className="text-green-400">{position.earned}</span></div>
                    </div>
                  </div>
                  
                  <div className={`text-right ${isMobile ? 'flex justify-end' : ''}`}>
                    <div className={`font-medium text-xs px-3 py-1 rounded-full ${
                      position.status === 'Active' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    }`}>
                      {position.status}
                    </div>
                  </div>
                </div>
              </MobileOptimizedMotion>
            ))}
          </div>
        </div>
      </MobileOptimizedMotion>

      {/* Add Funds Modal */}
      {showAddFundsModal && (
        <AddFundsModal isOpen={showAddFundsModal} onClose={() => setShowAddFundsModal(false)} />
      )}
    </div>
  );
};

export default Vault; 